import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Upload, AlertTriangle, Trash2, Plus, Clock, Shield, Eye, Calendar, User, Hash, Building2, StickyNote } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday } from '@/lib/dateRange';

const CATEGORIES = ['contract', 'id_card', 'passport', 'visa', 'work_permit', 'medical', 'certificate', 'license', 'insurance', 'other'];

const catLabel = (c) => (c || 'other').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

export default function EmployeeDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const { t } = useLanguage();
  const [alerts, setAlerts] = useState({ expired: [], expiring_soon: [], total_alerts: 0 });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [form, setForm] = useState({ employee_id: '', title: '', category: 'other', document_number: '', issue_date: '', expiry_date: '', issuing_authority: '', notes: '' });

  const fetchData = async () => {
    try {
      const [docsRes, alertsRes, empRes] = await Promise.all([
        api.get('/employee-documents', { params: { employee_id: filterEmp || undefined, category: filterCat || undefined } }),
        api.get('/employee-documents/expiry-alerts', { params: { days: 60 } }),
        api.get('/employees'),
      ]);
      setDocuments(docsRes.data);
      setAlerts(alertsRes.data);
      setEmployees(empRes.data.filter(e => e.status === 'active' || e.active));
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterEmp, filterCat]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.title) { toast.error('Employee and title required'); return; }
    try {
      await api.post('/employee-documents', form);
      toast.success('Document added');
      setShowDialog(false);
      setForm({ employee_id: '', title: '', category: 'other', document_number: '', issue_date: '', expiry_date: '', issuing_authority: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create document');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/employee-documents/${id}`);
      toast.success('Document deleted');
      if (viewDoc?.id === id) setViewDoc(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const isExpired = (date) => date && date < localToday();
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 60;
  };

  const getStatusBadge = (expiryDate) => {
    if (!expiryDate) return <Badge variant="outline" className="text-[10px]">No Expiry</Badge>;
    if (isExpired(expiryDate)) return <Badge variant="destructive" className="text-[10px]">Expired</Badge>;
    if (isExpiringSoon(expiryDate)) return <Badge className="text-[10px] bg-warning">Expiring Soon</Badge>;
    return <Badge variant="default" className="text-[10px] bg-success">Valid</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="employee-documents-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-sky-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
                <FileText size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('emp_docs_title') || 'Employee Documents'}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Track documents, expiry dates, and compliance</p>
              </div>
            </div>
          </div>
          <Button onClick={() => setShowDialog(true)} data-testid="add-document-btn"><Plus size={14} className="mr-1.5" /> Add Document</Button>
        </div>

        {/* Alert Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="stat-total"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><FileText size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{documents.length}</div><div className="text-xs text-muted-foreground">Total Documents</div></div></div></CardContent></Card>
          <Card className={alerts.expired?.length > 0 ? 'border-error/30 bg-error/10' : ''} data-testid="stat-expired"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-error/10 rounded-lg"><AlertTriangle size={18} className="text-error" /></div><div><div className="text-2xl font-bold text-error">{alerts.expired?.length || 0}</div><div className="text-xs text-muted-foreground">Expired</div></div></div></CardContent></Card>
          <Card className={alerts.expiring_soon?.length > 0 ? 'border-warning/30 bg-warning/10' : ''} data-testid="stat-expiring"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><Clock size={18} className="text-warning" /></div><div><div className="text-2xl font-bold text-warning">{alerts.expiring_soon?.length || 0}</div><div className="text-xs text-muted-foreground">Expiring (60 days)</div></div></div></CardContent></Card>
        </div>

        <Tabs defaultValue="all" className="space-y-4">
          <TabsList><TabsTrigger value="all" data-testid="tab-all">All Documents</TabsTrigger><TabsTrigger value="alerts" data-testid="tab-alerts">Expiry Alerts ({alerts.total_alerts})</TabsTrigger></TabsList>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4 flex-wrap">
                  <CardTitle className="text-base">Documents</CardTitle>
                  <Select value={filterEmp || 'all'} onValueChange={(v) => setFilterEmp(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-48" data-testid="filter-emp"><SelectValue placeholder="All Employees" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Employees</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filterCat || 'all'} onValueChange={(v) => setFilterCat(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-40" data-testid="filter-cat"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">No documents found</p>
                    <p className="text-xs mt-1">Add your first employee document above</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {documents.map((d) => (
                      <div key={d.id}
                        className="flex items-center justify-between p-3.5 rounded-xl border border-border hover:border-border hover:shadow-sm transition-all cursor-pointer bg-white group"
                        onClick={() => setViewDoc(d)}
                        data-testid={`doc-row-${d.id}`}>
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            isExpired(d.expiry_date) ? 'bg-error/10' : isExpiringSoon(d.expiry_date) ? 'bg-warning/10' : 'bg-info/10'
                          }`}>
                            <FileText size={18} className={
                              isExpired(d.expiry_date) ? 'text-error' : isExpiringSoon(d.expiry_date) ? 'text-warning' : 'text-info'
                            } />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-foreground truncate">{d.title || catLabel(d.category)}</span>
                              <Badge variant="outline" className="text-[10px] capitalize shrink-0">{catLabel(d.category)}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><User size={11} />{d.employee_name || 'Unknown'}</span>
                              {d.document_number && <span className="flex items-center gap-1"><Hash size={11} />{d.document_number}</span>}
                              {d.expiry_date && <span className="flex items-center gap-1"><Calendar size={11} />Exp: {d.expiry_date}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(d.expiry_date)}
                          <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setViewDoc(d); }} data-testid={`view-doc-${d.id}`}>
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-error opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }} data-testid={`delete-doc-${d.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts">
            <div className="space-y-4">
              {alerts.expired?.length > 0 && (
                <Card className="border-error/30">
                  <CardHeader><CardTitle className="text-base text-error flex items-center gap-2"><AlertTriangle size={16} /> Expired Documents</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {alerts.expired.map(d => (
                        <div key={d.id} className="flex items-center justify-between bg-error/10 rounded-lg p-3 border border-error/30 cursor-pointer hover:bg-error/20 transition-colors" onClick={() => setViewDoc(d)} data-testid={`expired-${d.id}`}>
                          <div><span className="font-medium text-sm">{d.employee_name}</span> - <span className="text-sm">{d.title || catLabel(d.category)}</span> <Badge variant="outline" className="text-[10px] capitalize ml-1">{catLabel(d.category)}</Badge></div>
                          <Badge variant="destructive" className="text-xs">Expired: {d.expiry_date}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {alerts.expiring_soon?.length > 0 && (
                <Card className="border-warning/30">
                  <CardHeader><CardTitle className="text-base text-warning flex items-center gap-2"><Clock size={16} /> Expiring Soon (60 days)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {alerts.expiring_soon.map(d => (
                        <div key={d.id} className="flex items-center justify-between bg-warning/10 rounded-lg p-3 border border-warning/30 cursor-pointer hover:bg-warning/20 transition-colors" onClick={() => setViewDoc(d)} data-testid={`expiring-${d.id}`}>
                          <div><span className="font-medium text-sm">{d.employee_name}</span> - <span className="text-sm">{d.title || catLabel(d.category)}</span></div>
                          <Badge variant="secondary" className="text-xs">Expires: {d.expiry_date}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {alerts.total_alerts === 0 && <div className="text-center py-12 text-muted-foreground"><Shield className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm font-medium">All clear!</p><p className="text-xs mt-1">No expiry alerts — all documents are up to date</p></div>}
            </div>
          </TabsContent>
        </Tabs>

        {/* View Document Dialog */}
        <Dialog open={!!viewDoc} onOpenChange={(open) => !open && setViewDoc(null)}>
          <DialogContent className="max-w-lg" data-testid="view-doc-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={18} className="text-info" />
                {viewDoc?.title || catLabel(viewDoc?.category)}
              </DialogTitle>
            </DialogHeader>
            {viewDoc && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  {getStatusBadge(viewDoc.expiry_date)}
                  <Badge variant="outline" className="capitalize text-xs">{catLabel(viewDoc.category)}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <DetailItem icon={User} label="Employee" value={viewDoc.employee_name || 'Unknown'} />
                  <DetailItem icon={Hash} label="Document Number" value={viewDoc.document_number || '-'} />
                  <DetailItem icon={Calendar} label="Issue Date" value={viewDoc.issue_date || '-'} />
                  <DetailItem icon={Calendar} label="Expiry Date" value={viewDoc.expiry_date || 'No expiry'} highlight={isExpired(viewDoc.expiry_date) ? 'red' : isExpiringSoon(viewDoc.expiry_date) ? 'amber' : null} />
                  <DetailItem icon={Building2} label="Issuing Authority" value={viewDoc.issuing_authority || '-'} />
                  <DetailItem icon={FileText} label="Category" value={catLabel(viewDoc.category)} />
                </div>

                {viewDoc.notes && (
                  <div className="bg-muted/50 rounded-lg p-3 border border-border">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1"><StickyNote size={12} />Notes</div>
                    <p className="text-sm text-foreground">{viewDoc.notes}</p>
                  </div>
                )}

                {viewDoc.file_url && (
                  <a href={viewDoc.file_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-info/10 border border-info/30 text-info text-sm font-medium hover:bg-info/20 transition-colors" data-testid="view-file-link">
                    <Eye size={14} /> View Attached File
                  </a>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button size="sm" variant="ghost" className="text-error" onClick={() => { handleDelete(viewDoc.id); }}>
                    <Trash2 size={14} className="mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Document Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="add-doc-dialog">
            <DialogHeader><DialogTitle>Add Employee Document</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee *</Label>
                  <Select value={form.employee_id || 'none'} onValueChange={(v) => setForm({ ...form, employee_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="doc-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="doc-cat-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" required /></div>
                <div><Label>Document Number</Label><Input value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} /></div>
                <div><Label>Issuing Authority</Label><Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} /></div>
                <div><Label>Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="doc-expiry" /></div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button type="submit" className="w-full" data-testid="doc-submit-btn">Add Document</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function DetailItem({ icon: Icon, label, value, highlight }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Icon size={12} />{label}</div>
      <div className={`text-sm font-medium ${
        highlight === 'red' ? 'text-error' : highlight === 'amber' ? 'text-warning' : 'text-foreground'
      }`}>{value}</div>
    </div>
  );
}
